import { findBestMatch, pickParamCopyFields } from './parameter-comparison.js';
import type {
  WorkfrontCustomFormDetailsRaw,
  WorkfrontCustomFormGroup,
  WorkfrontCustomFormModel,
  WorkfrontCustomFormParameter,
  WorkfrontWorkspaceContext,
} from './types.js';
import type { CustomFormsApi } from './api.js';

/**
 * High-level clone orchestration. Mirrors the legacy
 * `wf-audit/src/slices/workfront/components/custom-forms-migration/
 * custom-form/actions.ts` — a clone proceeds in five steps:
 *
 *   1. Validate name (target has no existing form with this name)
 *   2. Match groups + params on the target (`getMatched*` ops)
 *   3. Create missing groups (`copyCustomFormGroups`)
 *   4. Create missing params (`copyCustomFormParams`, one per param)
 *   5. Create the form referencing the resulting parameter ids
 *      (`copyCustomForm`)
 *
 * Each step logs progress via the `log` callback so the wizard can
 * stream a console-style transcript while it runs.
 *
 * Only one adaptation vs the legacy: API client comes in via
 * dependency injection (`api`) so the action is testable without
 * mocking the global `fetch`.
 */

export function resolveActionError(error: unknown, fallback: string): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return fallback;
}

// ── Data transforms ─────────────────────────────────────────────────

function sortByDisplayOrder(
  parameters: WorkfrontCustomFormParameter[],
): WorkfrontCustomFormParameter[] {
  return [...parameters].sort((a, b) => a.displayOrder - b.displayOrder);
}

function getParameterGroups(data: WorkfrontCustomFormDetailsRaw) {
  const groupsData = data.categoryParameters.map(({ parameterGroup }) => ({
    id: parameterGroup.ID,
    name: parameterGroup.name,
    description: parameterGroup.description,
    isDefault: parameterGroup.isDefault,
    isCollapsed: parameterGroup.isCollapsed,
    displayOrder: parameterGroup.displayOrder,
  }));

  const defaultGroupId = groupsData.find(({ isDefault }) => isDefault)?.id;
  const uniqueOtherGroupIds = Array.from(
    new Set(groupsData.filter(({ isDefault }) => !isDefault).map(({ id }) => id)),
  );
  const groupIds = defaultGroupId ? [defaultGroupId, ...uniqueOtherGroupIds] : uniqueOtherGroupIds;

  return groupIds.map((id) => groupsData.find((group) => group.id === id)!);
}

export function getFormDataGroups(
  data?: WorkfrontCustomFormDetailsRaw,
): WorkfrontCustomFormGroup[] {
  if (!data) return [];

  const groups = getParameterGroups(data);

  return groups.map((group) => {
    const params = data.categoryParameters
      .filter(({ parameterGroup }) => parameterGroup.ID === group.id)
      .map(
        ({
          displayOrder,
          rowShared,
          categoryParameterExpression,
          isRequired: isRequiredParamGroup,
          isReadOnly,
          isVisible,
          width,
          rowSpan,
          columnSpan,
          defaultValue,
          securityLevel,
          viewSecurityLevel,
          parameter,
        }): WorkfrontCustomFormParameter => {
          const {
            ID,
            customerID: _customerID,
            lastUpdateDate: _lastUpdateDate,
            lastUpdatedByID: _lastUpdatedByID,
            parameterDescriptiveText,
            parameterOptions,
            ...parameterFields
          } = parameter;

          const {
            customerID: _descriptiveTextCustomerID,
            parameterID: _descriptiveTextParameterID,
            ...descriptiveText
          } = parameterDescriptiveText || {};

          const {
            ID: _expressionID,
            objCode: _expressionObjCode,
            customerID: _expressionCustomerID,
            ...expression
          } = categoryParameterExpression || {};

          return {
            ...parameterFields,
            id: ID,
            displayOrder,
            rowShared,
            isRequiredParamGroup,
            isReadOnly,
            isVisible,
            width,
            rowSpan,
            columnSpan,
            defaultValue,
            securityLevel,
            viewSecurityLevel,
            parameterDescriptiveText: Object.keys(descriptiveText).length ? descriptiveText : null,
            categoryParameterExpression: Object.keys(expression).length ? expression : null,
            parameterOptions: parameterOptions || [],
          };
        },
      );

    return {
      ...group,
      params: sortByDisplayOrder(params),
    };
  });
}

export function toCustomFormModel(
  details: WorkfrontCustomFormDetailsRaw,
): WorkfrontCustomFormModel {
  return {
    name: details.name,
    description: details.description,
    isActive: details.isActive,
    objTypes: details.objTypes,
    catObjCode: details.catObjCode,
    groupID: details.groupID,
    displayOrder: details.displayOrder,
    groups: getFormDataGroups(details),
  };
}

export function getParameterGroupsURLParams(formParameters: WorkfrontCustomFormModel): string {
  const queryParams: Record<string, string> = {};
  formParameters.groups.forEach((sourceParameter, index) => {
    queryParams[`OR:${index + 1}:name`] = sourceParameter.name;
  });
  return new URLSearchParams({
    fields: '*',
    $$LIMIT: '2000',
    $$FIRST: '0',
    ...queryParams,
  }).toString();
}

export function getParametersURLParams(formParameters: WorkfrontCustomFormModel): string {
  const queryParams: Record<string, string> = {};
  let index = 0;
  formParameters.groups.forEach(({ params }) => {
    params.forEach((parameter) => {
      index += 1;
      queryParams[`OR:${index}:name`] = parameter.name;
    });
  });
  return new URLSearchParams({
    fields: '*',
    $$LIMIT: '2000',
    $$FIRST: '0',
    ...queryParams,
  }).toString();
}

// ── API operations ──────────────────────────────────────────────────

export async function validateFormName(
  api: CustomFormsApi,
  name: string,
  workspace: WorkfrontWorkspaceContext,
): Promise<void> {
  const response = await api.getCustomForms(workspace);
  if (response.error) throw new Error(response.error.message);
  const existingNames = (response.data || []).map(({ name }) => name);
  if (existingNames.includes(name)) {
    throw new Error(`A custom form with the name "${name}" already exists in the target instance.`);
  }
}

export async function getMatchedFormResults(
  api: CustomFormsApi,
  formDetails: WorkfrontCustomFormModel,
  destinationWorkspace: WorkfrontWorkspaceContext,
): Promise<WorkfrontCustomFormGroup[]> {
  const [matchedGroupsResponse, matchedParamsResponse] = await Promise.all([
    api.getMatchedParameterGroups(destinationWorkspace, getParameterGroupsURLParams(formDetails)),
    api.getMatchedParameters(destinationWorkspace, getParametersURLParams(formDetails)),
  ]);

  if (matchedGroupsResponse.error) throw new Error(matchedGroupsResponse.error.message);
  if (matchedParamsResponse.error) throw new Error(matchedParamsResponse.error.message);

  const matchedGroups = matchedGroupsResponse.data || [];
  const matchedParams = matchedParamsResponse.data || [];

  return formDetails.groups.map((group) => {
    const groupInDestination = matchedGroups.find(({ name }) => group.name === name);

    const params = group.params.map((parameter) => {
      const result = findBestMatch(parameter, matchedParams);
      return {
        ...parameter,
        destId: result ? result.match.ID : null,
        destName: result ? result.match.name : null,
        mismatch: result ? result.mismatch : null,
      };
    });

    return {
      ...group,
      destId: groupInDestination ? groupInDestination.ID : null,
      destName: groupInDestination ? groupInDestination.name : null,
      params,
    };
  });
}

export async function copyCustomFormGroups(
  api: CustomFormsApi,
  formData: WorkfrontCustomFormGroup[],
  destinationWorkspace: WorkfrontWorkspaceContext,
  log: (message: string) => void,
): Promise<{ formData: WorkfrontCustomFormGroup[]; errors: string[] }> {
  log('Copying custom form groups...');

  const groups = formData
    .filter(({ destId }) => !destId)
    .map(({ name, description, isCollapsed, displayOrder }) => ({
      name,
      description,
      isCollapsed,
      displayOrder,
    }));

  if (!groups.length) {
    log('No groups were found to copy');
    return { formData, errors: [] };
  }

  log(`Copying the following groups: ${groups.map((group) => group.name).join(', ')}...`);

  // Workfront `attask/api/v21.0/PGRP` accepts a bulk-create array
  // body and returns `Array<{ID, name}>`. The engine schema accepts
  // either object or array params; we forward as-is.
  const response = await api.copyCustomFormGroups(destinationWorkspace, groups);

  if (response.error) {
    // Bug fix 2026-05-18: previously this only logged and continued,
    // ending up with `parameterGroupID: null` on every param in the
    // failed group → cloned form rendered without group structure
    // and the operator had to delete + retry from scratch. Push the
    // failure into `errors` so the orchestrator aborts before
    // creating the form on the target.
    log(`Error copying groups: ${response.error.message}`);
    return {
      formData,
      errors: [`copyCustomFormGroups: ${response.error.message}`],
    };
  }
  log('Custom form groups copied');

  const copiedGroups = response.data || [];

  const updated = formData.map((group) => {
    if (group.destId) return group;
    const groupInDestination = copiedGroups.find(({ name }) => group.name === name);
    return {
      ...group,
      destId: groupInDestination ? groupInDestination.ID : null,
      destName: groupInDestination ? groupInDestination.name : null,
    };
  });

  // Double-check every group that needed a copy got a destId back —
  // partial Workfront responses (created some, dropped others) are a
  // documented edge case in big tenants. Halt before the form-create
  // step in that case too.
  const stillMissing = updated.filter((g) => !g.destId);
  if (stillMissing.length > 0) {
    const names = stillMissing.map((g) => g.name).join(', ');
    log(`Group copy returned no destId for: ${names}`);
    return {
      formData: updated,
      errors: [
        `copyCustomFormGroups: Workfront did not return a destId for ${stillMissing.length} group(s): ${names}`,
      ],
    };
  }

  return { formData: updated, errors: [] };
}

export async function copyCustomFormParams(
  api: CustomFormsApi,
  formData: WorkfrontCustomFormGroup[],
  destinationWorkspace: WorkfrontWorkspaceContext,
  log: (message: string) => void,
): Promise<{ errors: string[]; updatedFormData: WorkfrontCustomFormGroup[] }> {
  const updatedFormData: WorkfrontCustomFormGroup[] = [];
  const errors: string[] = [];

  for (const group of formData) {
    const updatedParams: WorkfrontCustomFormParameter[] = [];

    for (const parameter of group.params) {
      if (parameter.destId) {
        updatedParams.push(parameter);
        continue;
      }

      log(`Copying parameter ${parameter.name}...`);

      const copyFields = pickParamCopyFields(parameter);
      const body = {
        ...copyFields,
        parameterOptions: (parameter.parameterOptions || []).map(
          ({ ID: _id, ...option }) => option,
        ),
      };

      const response = await api.copyCustomFormParams(destinationWorkspace, body);

      if (response.error) {
        const message = response.error.message;
        log(`Error encountered: ${message}`);
        errors.push(`${parameter.name} error: ${message}`);
      } else {
        log(`Parameter ${parameter.name} copied`);
      }

      updatedParams.push({
        ...parameter,
        destId: response.data?.ID ?? null,
        destName: response.data?.name ?? null,
      });
    }

    updatedFormData.push({
      ...group,
      params: updatedParams,
    });
  }

  return { errors, updatedFormData };
}

// ── High-level flows ────────────────────────────────────────────────

export async function copyCustomForm(
  api: CustomFormsApi,
  formDetails: WorkfrontCustomFormModel,
  matchedFormResults: WorkfrontCustomFormGroup[],
  destinationConfig: WorkfrontWorkspaceContext & { formId?: string | null },
  log: (message: string) => void,
): Promise<{ formResults: WorkfrontCustomFormGroup[]; errors: string[]; formId?: string }> {
  log('Copying a custom form...');

  const workspace: WorkfrontWorkspaceContext = {
    workspaceRef: destinationConfig.workspaceRef,
  };

  const groupResult = await copyCustomFormGroups(api, matchedFormResults, workspace, log);
  if (groupResult.errors.length > 0) {
    // Group-copy failure → abort before touching params + the final
    // form-create POST. Continuing leaves the target with orphaned
    // parameters (created on PARAM, never attached to a CTGY) and
    // a broken form referencing `parameterGroupID: null` — a worse
    // state than just not cloning at all.
    log(`Aborting clone — group copy failed:\n${groupResult.errors.join('\n')}`);
    return { formResults: groupResult.formData, errors: groupResult.errors };
  }
  const updatedGroupResults = groupResult.formData;

  const { errors, updatedFormData } = await copyCustomFormParams(
    api,
    updatedGroupResults,
    workspace,
    log,
  );

  if (errors.length) {
    log(`Error encountered:\n${errors.join('\n')}`);
    return { formResults: updatedFormData, errors };
  }

  const categoryParameters = updatedFormData.flatMap(({ destId, params }) =>
    params.map((parameter) => ({
      displayOrder: parameter.displayOrder,
      parameterID: parameter.destId,
      rowShared: parameter.rowShared,
      isRequired: parameter.isRequiredParamGroup,
      isReadOnly: parameter.isReadOnly,
      isVisible: parameter.isVisible,
      width: parameter.width,
      rowSpan: parameter.rowSpan,
      columnSpan: parameter.columnSpan,
      defaultValue: parameter.defaultValue,
      securityLevel: parameter.securityLevel,
      viewSecurityLevel: parameter.viewSecurityLevel,
      categoryParameterExpression: parameter.categoryParameterExpression,
      parameterGroupID: destId,
    })),
  );

  const body = {
    name: formDetails.name,
    description: formDetails.description,
    isActive: formDetails.isActive,
    catObjCode: formDetails.catObjCode,
    objTypes: formDetails.objTypes,
    categoryParameters,
  };

  const response = await api.copyCustomForm(workspace, body, destinationConfig.formId || undefined);

  if (response.error) {
    log(`Error encountered: ${response.error.message}`);
    return { formResults: updatedFormData, errors: [response.error.message] };
  }

  const formId =
    response.data && typeof response.data === 'object' && 'ID' in response.data
      ? String(response.data.ID)
      : undefined;

  log('Custom form copied.');

  return { formResults: updatedFormData, errors: [], formId };
}

export async function cloneCustomForm(
  api: CustomFormsApi,
  formDetails: WorkfrontCustomFormModel,
  destinationWorkspace: WorkfrontWorkspaceContext,
  newName: string,
  log: (message: string) => void,
): Promise<{ errors: string[]; formId?: string }> {
  log(`Validating name "${newName}"...`);
  await validateFormName(api, newName, destinationWorkspace);

  log('Matching groups and parameters in target...');
  const matchedResults = await getMatchedFormResults(api, formDetails, destinationWorkspace);

  const { errors, formId } = await copyCustomForm(
    api,
    { ...formDetails, name: newName },
    matchedResults,
    destinationWorkspace,
    log,
  );

  if (errors.length) return { errors };

  log(`Custom form cloned as "${newName}".`);
  return { errors: [], formId };
}
